import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Home,
  PlayCircle,
  Video,
  MessageCircle,
  Users,
  LogOut,
  Settings,
  HelpCircle
} from 'lucide-react';

import { School } from '@mui/icons-material';


const drawerWidthOpen = 260;
const drawerWidthClosed = 76;

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? drawerWidthOpen : drawerWidthClosed,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: open ? drawerWidthOpen : drawerWidthClosed,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
    overflowX: 'hidden',
    backgroundColor: theme.palette.mode === 'light' ? '#1a237e' : '#121212',
    color: theme.palette.common.white,
    borderRight: 'none',
    boxShadow: '0 0 20px rgba(0, 0, 0, 0.15)',
  },
}));

const MenuItem = styled(ListItemButton)(({ theme, active }) => ({
  minHeight: 54,
  borderRadius: '12px',
  margin: '8px 10px',
  padding: '0 14px',
  backgroundColor: active ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  transition: theme.transitions.create(['background-color', 'transform'], {
    duration: 0.2,
  }),
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const LogoWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2),
  height: 70,
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.5rem',
  background: 'linear-gradient(45deg, #7c4dff 30%, #448aff 90%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  letterSpacing: 1,
}));

const UserSection = styled(Box)(({ theme, open }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: open ? 'flex-start' : 'center',
  marginTop: 'auto',
  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
  backgroundColor: 'rgba(0, 0, 0, 0.15)',
}));

const Sidebar = ({ course, expanded = true }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [activeItem, setActiveItem] = useState('Home');

  const handleNavigation = (path, itemName) => {
    setActiveItem(itemName);
    navigate(path);
  };

  // console.log(JSON.stringify(course, null , 2));
  // console.log('Course ', course);
  const menuItems = [
    {
      text: 'Home',
      icon: <Home size={22} />,
      path: '/'
    },
    {
      text: 'Continue Learning',
      icon: <School size={22} />,  // MUI graduation cap/school icon
      path: `/course-learn/${course.deckId}`
    },
    {
      text: 'View Course',
      icon: <PlayCircle size={22} />,
      path: `/view-course/${course._id}`
    },

    {
      text: 'Conversations',
      icon: <MessageCircle size={22} />,
      path: '/conversations'
    }
  ];

  const secondaryMenuItems = [
    {
      text: 'Settings',
      icon: <Settings size={22} />,
      path: '/settings'
    },
    {
      text: 'Help',
      icon: <HelpCircle size={22} />,
      path: '/help'
    }
  ];

  return (
    <StyledDrawer variant="permanent" open={isExpanded}>
      <LogoWrapper
        sx={{
          justifyContent: isExpanded ? 'space-between' : 'center',
        }}
      >
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <Logo variant="h6">ankiverse.com</Logo>
            </motion.div>
          )}
        </AnimatePresence>
        <IconButton
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white'
            }
          }}
          size="small"
        >
          {isExpanded ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </LogoWrapper>

      <Divider sx={{
        opacity: 0.2,
        borderColor: 'rgba(255, 255, 255, 0.12)',
        mx: isExpanded ? 2 : 1
      }} />

      <Box sx={{ overflow: 'auto', mt: 1 }}>
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
              <Tooltip
                title={!isExpanded ? item.text : ''}
                placement="right"
                arrow
              >
                <MenuItem
                  onClick={() => handleNavigation(item.path, item.text)}
                  active={activeItem === item.text ? 1 : 0}
                  sx={{
                    justifyContent: isExpanded ? 'initial' : 'center',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isExpanded ? 3 : 'auto',
                      justifyContent: 'center',
                      color: activeItem === item.text ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontWeight: activeItem === item.text ? 600 : 400,
                            fontSize: '0.95rem',
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </MenuItem>
              </Tooltip>
            </ListItem>
          ))}
        </List>

        <Divider sx={{
          opacity: 0.2,
          borderColor: 'rgba(255, 255, 255, 0.12)',
          mx: isExpanded ? 2 : 1,
          my: 2
        }} />

        <List>
          {secondaryMenuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
              <Tooltip
                title={!isExpanded ? item.text : ''}
                placement="right"
                arrow
              >
                <MenuItem
                  onClick={() => handleNavigation(item.path, item.text)}
                  active={activeItem === item.text ? 1 : 0}
                  sx={{
                    justifyContent: isExpanded ? 'initial' : 'center',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isExpanded ? 3 : 'auto',
                      justifyContent: 'center',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontWeight: activeItem === item.text ? 600 : 400,
                            fontSize: '0.95rem',
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </MenuItem>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>

      <UserSection open={isExpanded}>
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: 'primary.light',
            border: '2px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          U
        </Avatar>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden', marginLeft: 12 }}
            >
              <Typography variant="body2" fontWeight={500}>
                User Name
              </Typography>
              <Typography variant="caption" color="rgba(255, 255, 255, 0.6)">
                student@example.com
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
        <IconButton
          size="small"
          sx={{
            ml: 'auto',
            color: 'rgba(255, 255, 255, 0.7)',
            display: isExpanded ? 'flex' : 'none',
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
          onClick={() => handleNavigation('/logout', 'Logout')}
        >
          <LogOut size={18} />
        </IconButton>
      </UserSection>
    </StyledDrawer>
  );
};

export default Sidebar;